// #336 slice 5: the round actions that follow the rules (brief §5.2.3,
// §5.4b). A round's type, a round taken out, four rounds added (flow 9):
// each slot's rule is read before the change and written again after it, so
// a deload round gets its effect and the progression resumes after it. The
// cells «a mano» stay, and move with their round. A slot with no rule is
// left as the structural action leaves it. A superset takes the slot's set
// count, as applyRule does (Q7). Keep free of React and any UI import.
import type { Draft } from "./programEdit.js";
import { MAX_CYCLE_WEEKS, setCycleWeeks } from "./programEditActions.js";
import { cloneWeekInDraft } from "./programEditWeekClone.js";
import { removeWeekAt } from "./programMatrixEdits.js";
import {
  DEFAULT_LOAD_STEP,
  defaultDeload,
  isEffortAxis,
  liftSeq,
  seqAt,
  type GenerateOptions,
  type ProgressionRule,
  type RuleException,
  type Sequence,
} from "./programProgression.js";
import { applyRule, draftRoundTypes, recognizeWith, type SlotRecognizer } from "./programProgressionApply.js";
import { setRoundType } from "./programRoundTypes.js";
import type { RoundType } from "./schemas/programV3.js";

type RoundOptions = GenerateOptions & { lockedThrough?: number; recognize?: SlotRecognizer };

/** Every slot of `before` that follows a rule, written again into `after`
 *  from round `from` on. `mapWeek` takes a round of `before` to its place in
 *  `after` (null: gone), so an exception moves with its round. */
const rewrite = (
  before: Draft,
  after: Draft,
  mapWeek: (w: number) => number | null,
  from: number,
  opts: RoundOptions,
  pick: (rule: ProgressionRule) => ProgressionRule | null = (r) => r,
): Draft => {
  const oldTypes = draftRoundTypes(before);
  let next = after;
  before.sessions.forEach((session, d) => {
    for (const slot of session.slots) {
      const rec = recognizeWith(slot, oldTypes, opts);
      if (rec.kind === "manual") continue;
      const rule = pick(rec.rule);
      const j = next.sessions[d]?.slots.findIndex((s) => s.id === slot.id) ?? -1;
      const target = next.sessions[d]?.slots[j];
      if (!rule || !target) continue;
      const keep: RuleException[] = rec.exceptions.flatMap((e) => {
        const w = mapWeek(e.week);
        return w === null ? [] : [{ ...e, week: w }];
      });
      next = applyRule(next, { day: d, slot: j }, rule, { ...opts, from, keep });
    }
  });
  return next;
};

/** A rule with no deload of its own takes defaultDeload (§5.3) when a round
 *  becomes a deload; one that has its own keeps it. */
const withDeloadFor = (r: ProgressionRule, type: RoundType): ProgressionRule =>
  type === "deload" && r.deload === undefined ? { ...r, deload: defaultDeload(r.axis) } : r;

/** Round `week` given `type`, every rule following it («Applicato a: tutto il
 *  programma»). A rule with no deload of its own takes defaultDeload (§5.3). An
 *  executed round: the same draft. */
export const setRoundTypeByRules = (draft: Draft, week: number, type: RoundType, opts: RoundOptions = {}): Draft => {
  if (week <= (opts.lockedThrough ?? -1)) return draft;
  const typed = setRoundType(draft, week, type);
  if (typed === draft) return draft;
  return rewrite(draft, typed, (w) => w, week, opts, (r) => withDeloadFor(r, type));
};

/** Round `week` taken out; the cells «a mano» after it move with their round,
 *  the rest follows the rule again. An executed round: the same draft. */
export const removeRoundByRules = (draft: Draft, week: number, opts: RoundOptions = {}): Draft => {
  if (week <= (opts.lockedThrough ?? -1)) return draft;
  const removed = removeWeekAt(draft, week);
  if (removed === draft) return draft;
  return rewrite(draft, removed, (w) => (w === week ? null : w > week ? w - 1 : w), week, opts);
};

export type NextBlockResult = { ok: true; draft: Draft } | { ok: false; reason: "full" };

/** A rule that continues from where it was in the next block: a load row, a
 *  threshold, or reps or seconds that move. Effort and % rows repeat the
 *  shape instead (§5.4b flow 9: the load rises with the measured MAX). */
export const ruleContinues = (r: ProgressionRule): boolean =>
  r.axis === "absolute" || r.threshold !== undefined || (r.reps.kind !== "constant" && !isEffortAxis(r.axis));

/** #400, the double progression: reps that move inside a range start their
 *  sequence again in the next block (restartRule). Continued, they would
 *  leave the range. A threshold restarts by itself. */
export const ruleRestarts = (r: ProgressionRule): boolean =>
  r.range !== undefined && r.threshold === undefined && r.reps.kind !== "constant" && !isEffortAxis(r.axis);

/** What a rule does in the next block: it continues, restarts (#400) or
 *  repeats the rounds it copies. addNextBlock and «More time» read this. */
export type BlockMode = "continue" | "restart" | "repeat";
export const nextBlockMode = (r: ProgressionRule): BlockMode =>
  ruleRestarts(r) ? "restart" : ruleContinues(r) ? "continue" : "repeat";

/** `seq` holds one value over rounds `from` on. */
const flatFrom = (seq: Sequence, types: readonly RoundType[], from: number): boolean =>
  types.every((_, w) => w < from || seqAt(seq, w, types) === seqAt(seq, from, types));

/** The rule of a restart from round `from`: the reps from their first round
 *  again, typed by the new rounds. A written load that holds over the block
 *  goes up one `step`; one that climbs already climbs on, never down. */
const restartRule = (r: ProgressionRule, from: number, types: readonly RoundType[], step: number): ProgressionRule => {
  const reps: Sequence = { kind: "blocks", blocks: [{ rounds: from, seq: r.reps }, { rounds: types.length - from, seq: r.reps }] };
  const value = r.axis === "absolute" && r.value && flatFrom(r.value, types, from) ? liftSeq(r.value, step) : r.value;
  return { ...r, reps, value };
};

/** The reps of a deload or test round of the block before that differ from
 *  the rule, kept in the new round copied from it (`source`) when that has
 *  the same type: a wave the recognizer reads as a line (8, 10, 12 and an
 *  easy 8) keeps its easy round. Normal rounds and any other cell by hand
 *  follow the rule. */
const carriedReps = (
  exceptions: readonly RuleException[],
  from: number,
  k: number,
  source: (j: number) => number,
  oldTypes: readonly RoundType[],
  types: readonly RoundType[],
): RuleException[] =>
  Array.from({ length: k }, (_, j) => j).flatMap((j) => {
    const w = source(j);
    const type = oldTypes[w];
    if ((type !== "deload" && type !== "test") || types[from + j] !== type) return [];
    return exceptions.filter((e) => e.week === w && e.field === "reps").map((e) => ({ ...e, week: from + j }));
  });

/** A rule that repeats copies its rounds, which holds while each new round has
 *  the type of its source. When one differs (`types` given) the copied cells
 *  are those of another type: the rule is written again on the new types, and
 *  the cells by hand are kept only in the rounds whose type did not change.
 *  null: every type is the same, the copy stands. */
const repeatedCells = (
  exceptions: readonly RuleException[],
  from: number,
  k: number,
  source: (j: number) => number,
  oldTypes: readonly RoundType[],
  types: readonly RoundType[],
): RuleException[] | null => {
  const js = Array.from({ length: k }, (_, j) => j);
  const same = (j: number) => (oldTypes[source(j)] ?? "normal") === types[from + j];
  if (js.every(same)) return null;
  return js.filter(same).flatMap((j) => exceptions.filter((e) => e.week === source(j)).map((e) => ({ ...e, week: from + j })));
};

/** Flow 9: `rounds` more rounds (default 4) after the last. Each copies the
 *  round `rounds` places before it (cloneWeekInDraft) with its type, or the
 *  type in `types`. Each slot's rule is read once: one that continues is
 *  written on from there, one that restarts is written again from its first
 *  round (restartRule), both on the new rounds' types and with no cell by hand
 *  but carriedReps, which the copy already holds. One that repeats keeps the
 *  copy, unless the new types differ from the copied ones (repeatedCells).
 *  "full": the cycle would pass MAX_CYCLE_WEEKS. */
export const addNextBlock = (
  draft: Draft,
  opts: RoundOptions & { rounds?: number; types?: readonly RoundType[] } = {},
): NextBlockResult => {
  const n = draft.cycleWeeks;
  const k = opts.rounds ?? 4;
  if (k < 1 || n + k > MAX_CYCLE_WEEKS) return { ok: false, reason: "full" };
  const oldTypes = draftRoundTypes(draft);
  const source = (j: number) => (n >= k ? n - k + j : j % n);
  let next = setCycleWeeks(draft, n + k, "blank");
  for (let j = 0; j < k; j += 1) {
    const cloned = cloneWeekInDraft(next, source(j), [n + j]);
    if (cloned.ok) next = cloned.draft;
    next = setRoundType(next, n + j, opts.types?.[j] ?? oldTypes[source(j)] ?? "normal");
  }
  const types = draftRoundTypes(next);
  const step = opts.loadQuantum ?? DEFAULT_LOAD_STEP;
  draft.sessions.forEach((session, d) => {
    for (const slot of session.slots) {
      const rec = recognizeWith(slot, oldTypes, opts);
      const j = next.sessions[d]?.slots.findIndex((s) => s.id === slot.id) ?? -1;
      const target = next.sessions[d]?.slots[j];
      if (rec.kind === "manual" || !target) continue;
      const mode = nextBlockMode(rec.rule);
      if (mode === "repeat") {
        const kept = repeatedCells(rec.exceptions, n, k, source, oldTypes, types);
        const rule = types.slice(n).includes("deload") ? withDeloadFor(rec.rule, "deload") : rec.rule;
        if (kept) next = applyRule(next, { day: d, slot: j }, rule, { ...opts, from: n, keep: kept });
        continue;
      }
      if (mode === "continue") {
        next = applyRule(next, { day: d, slot: j }, rec.rule, { ...opts, from: n, keep: [] });
        continue;
      }
      const keep = carriedReps(rec.exceptions, n, k, source, oldTypes, types);
      next = applyRule(next, { day: d, slot: j }, restartRule(rec.rule, n, types, step), { ...opts, from: n, keep });
    }
  });
  return { ok: true, draft: next };
};
